import { convertUtcToUserTimezone } from 'src/utils/CommonFunctions';

import { IEntityChatMessage } from './interface';

/**
 * Group messages by sender and time proximity (5-minute window), adding date separators.
 */
export const groupMessages = (messagesObj: IEntityChatMessage[], userTimezone?: string): IEntityChatMessage[] => {
    const groupedMessages: IEntityChatMessage[] = [];
    let currentGroup: IEntityChatMessage[] | null = null;
    let lastDate: string | null = null;

    messagesObj.forEach((message) => {
        if (message.special) {
            if (message.createdAt) {
                lastDate = userTimezone ? (convertUtcToUserTimezone(message.createdAt, 'YYYY-MM-DD', true, userTimezone) as string) : new Date(message.createdAt).toISOString().split('T')[0];
            }

            if (currentGroup) {
                groupedMessages.push(...currentGroup);
                currentGroup = null;
            }
            groupedMessages.push({ ...message, showSenderAndDate: false });

            return;
        }

        const messageDate = userTimezone ? (convertUtcToUserTimezone(message.createdAt, 'YYYY-MM-DD', true, userTimezone) as string) : new Date(message.createdAt).toISOString().split('T')[0];

        if (messageDate !== lastDate) {
            if (currentGroup) {
                groupedMessages.push(...currentGroup);
                currentGroup = null;
            }

            groupedMessages.push({
                id: `date-separator-${messageDate}`,
                content: '',
                userInfo: { id: '', firstName: '', lastName: '' },
                createdAt: message.createdAt,
                special: true,
                showSenderAndDate: false,
            });

            lastDate = messageDate;
        }

        const messageTime = new Date(message.createdAt).getTime();
        const lastMessageInGroupTime = currentGroup ? new Date(currentGroup[currentGroup.length - 1].createdAt).getTime() : null;

        if (currentGroup && message?.userInfo?.id === currentGroup[0]?.userInfo?.id && lastMessageInGroupTime && messageTime - lastMessageInGroupTime <= 5 * 60 * 1000) {
            currentGroup.push({ ...message, showSenderAndDate: false });
        } else {
            if (currentGroup) {
                groupedMessages.push(...currentGroup);
            }
            currentGroup = [{ ...message, showSenderAndDate: true }];
        }
    });

    if (currentGroup) {
        groupedMessages.push(...currentGroup);
    }

    return groupedMessages;
};

/**
 * Allowed MIME types for file attachments
 */
export const ALLOWED_FILE_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
export const MAX_FILES = 4;

export const getFileExtension = (filename?: string): string => {
    if (!filename) return '';
    const parts = filename.split('.');

    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
};

export const isDuplicateFile = (file: File, existingFiles: File[], currentBatch: File[] = []): boolean => {
    const allFiles = [...existingFiles, ...currentBatch];

    return allFiles.some((f) => f.name === file.name && f.size === file.size && f.type === file.type && f.lastModified === file.lastModified);
};

/**
 * Returns the socket room name for entity customer chat.
 * entityType: 'order' → 'order_customer_<id>'
 * entityType: 'quote' → 'quote_customer_<id>'
 */
export const getSocketRoomName = (entityId: string | number, entityType: 'order' | 'quote' = 'order'): string => `${entityType}_customer_${entityId}`;

const FILE_SIGNATURES: Record<string, number[][]> = {
    'image/jpeg': [[0xff, 0xd8, 0xff]],
    'image/jpg': [[0xff, 0xd8, 0xff]],
    'image/png': [[0x89, 0x50, 0x4e, 0x47]],
    'image/gif': [[0x47, 0x49, 0x46, 0x38]],
    'image/webp': [[0x52, 0x49, 0x46, 0x46]],
    'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
    'application/msword': [[0xd0, 0xcf, 0x11, 0xe0]],
    'application/vnd.ms-excel': [[0xd0, 0xcf, 0x11, 0xe0]],
    'application/vnd.ms-powerpoint': [[0xd0, 0xcf, 0x11, 0xe0]],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [[0x50, 0x4b, 0x03, 0x04]],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [[0x50, 0x4b, 0x03, 0x04]],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': [[0x50, 0x4b, 0x03, 0x04]],
};

/**
 * Returns true if the file is corrupt (empty or magic-byte mismatch).
 */
export const isCorruptFile = async (file: File): Promise<boolean> => {
    if (file.size === 0) return true;

    if (['text/plain', 'text/csv', 'image/svg+xml'].includes(file.type)) {
        return false;
    }

    const signatures = FILE_SIGNATURES[file.type];
    if (!signatures) return false;

    try {
        const headerSize = Math.max(...signatures.map((sig) => sig.length));
        const buffer = await file.slice(0, headerSize).arrayBuffer();
        const bytes = new Uint8Array(buffer);

        return !signatures.some((signature) => signature.every((byte, index) => bytes[index] === byte));
    } catch {
        return true;
    }
};

/** Sales rep shown in the chat header, already normalised for display. */
export interface IEntityChatSalesRep {
    name: string;
    image: string;
}

/**
 * Build the header's sales rep from an entity payload (order: salesRepFirstName/LastName +
 * salesImage/salesImageUrl; quote: user.firstName/lastName + image/imageUrl). The entity's own
 * rep is preferred over currentUser.salesRep because the latter needs an active customer_sales_rep
 * row on a non-NEW customer, which new/onboarding customers often lack — the order or quote still
 * knows who is handling it. Returns null when no name can be resolved.
 */
export const toEntityChatSalesRep = (rep?: { firstName?: string | null; lastName?: string | null; image?: string | null; imageUrl?: string | null } | null): IEntityChatSalesRep | null => {
    const name = [rep?.firstName, rep?.lastName].filter(Boolean).join(' ').trim();
    if (!name) return null;

    // wal-backend stores image (filename) + imageUrl (bucket base) separately; concat like every other avatar
    const image = rep?.imageUrl && rep?.image ? `${rep.imageUrl}${rep.image}` : rep?.image || '';

    return { name, image };
};
